import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../api'
import { adminFixtureClients, useAdminFixturesFromQuery } from '../fixtures'
import type { AdminClient } from '../types'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { Button } from '../../components/ui/Button'
import { formatWhen, safeAdminError } from '../safe-ui'

export function AdminClientsPage() {
  const fixtures = useAdminFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [clients, setClients] = useState<AdminClient[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [emailVerified, setEmailVerified] = useState('')
  const [hasTradingAccount, setHasTradingAccount] = useState('')

  async function load(nextPage = page) {
    // Keep filters + table shell visible; only dim while fetching.
    if (clients.length > 0) setRefreshing(true)
    else setLoading(true)
    setError('')
    try {
      if (fixtures) {
        let rows = [...adminFixtureClients]
        if (status) rows = rows.filter((c) => c.status === status)
        if (emailVerified === 'true') rows = rows.filter((c) => c.email_verified)
        if (emailVerified === 'false') rows = rows.filter((c) => !c.email_verified)
        if (hasTradingAccount === 'true') rows = rows.filter((c) => (c.demo_account_count || 0) > 0)
        if (hasTradingAccount === 'false') rows = rows.filter((c) => !(c.demo_account_count || 0))
        if (search.trim()) {
          const q = search.trim().toLowerCase()
          rows = rows.filter(
            (c) =>
              String(c.id).includes(q) ||
              (c.display_name || '').toLowerCase().includes(q) ||
              (c.email || '').toLowerCase().includes(q),
          )
        }
        setClients(rows)
        setPage(1)
        setTotalPages(1)
        return
      }
      const data = await adminApi.listClients({
        page: nextPage,
        pageSize: 50,
        sort: 'id',
        order: 'desc',
        search: search.trim() || undefined,
        status: status || undefined,
        emailVerified: emailVerified === '' ? undefined : emailVerified === 'true',
        hasTradingAccount: hasTradingAccount === '' ? undefined : hasTradingAccount === 'true',
      })
      setClients(data.clients || [])
      setPage(data.pagination?.page || nextPage)
      setTotalPages(data.pagination?.total_pages || 1)
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load clients'))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    void load(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtures])

  const q = fixtures ? '?fixtures=1' : ''

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Clients" title="Client directory" />

      {fixtures ? (
        <StateBlock tone="warn" title="Fixture mode · visual review only">
          Client rows are preview data.
        </StateBlock>
      ) : null}

      <div className="ap-filters">
        <label className="cp-field">
          Search
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="id, name, email"
          />
        </label>
        <label className="cp-field">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="SUSPENDED">SUSPENDED</option>
          </select>
        </label>
        <label className="cp-field">
          Email verified
          <select value={emailVerified} onChange={(e) => setEmailVerified(e.target.value)}>
            <option value="">All</option>
            <option value="true">Verified</option>
            <option value="false">Unverified</option>
          </select>
        </label>
        <label className="cp-field">
          Trading account
          <select value={hasTradingAccount} onChange={(e) => setHasTradingAccount(e.target.value)}>
            <option value="">All</option>
            <option value="true">Has account</option>
            <option value="false">No account</option>
          </select>
        </label>
        <Button
          variant="secondary"
          onClick={() => {
            setPage(1)
            void load(1)
          }}
        >
          Apply
        </Button>
      </div>

      {error ? <p className="cp-error">{error}</p> : null}
      {loading || refreshing ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          {refreshing ? 'Updating clients…' : 'Loading clients…'}
        </p>
      ) : null}

      <div className={`cp-panel${loading || refreshing ? ' is-refreshing' : ''}`}>
        <ResponsiveTable
          rows={clients}
          rowKey={(c) => String(c.id)}
          empty={loading ? 'Loading clients…' : 'No clients found.'}
          columns={[
            {
              key: 'client',
              header: 'Client',
              render: (c) => (
                <span>
                  <strong>{c.display_name || '—'}</strong>
                  <span className="cp-muted"> #{c.id}</span>
                </span>
              ),
            },
            { key: 'email', header: 'Email', render: (c) => c.email || '—' },
            {
              key: 'verified',
              header: 'Verified',
              render: (c) => (c.email_verified ? 'Yes' : 'No'),
            },
            { key: 'status', header: 'Status', render: (c) => c.status || '—' },
            {
              key: 'accounts',
              header: 'Demo Accounts',
              render: (c) => c.demo_account_count ?? 0,
            },
            {
              key: 'last_login',
              header: 'Last Login',
              render: (c) => formatWhen(c.last_login_at),
            },
            {
              key: 'registered',
              header: 'Registered',
              render: (c) => formatWhen(c.registered_at),
            },
            {
              key: 'open',
              header: 'Open',
              render: (c) => (
                <Link className="pp-btn pp-btn--secondary pp-btn--sm" to={`/admin/clients/${c.id}${q}`}>
                  Open
                </Link>
              ),
            },
          ]}
        />
        <div className="ap-pager">
          <Button
            variant="ghost"
            disabled={page <= 1 || fixtures || loading || refreshing}
            onClick={() => void load(page - 1)}
          >
            Prev
          </Button>
          <span className="cp-muted">
            Page {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            disabled={page >= totalPages || fixtures || loading || refreshing}
            onClick={() => void load(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </section>
  )
}
